import type { ReactNode } from "react";
import { TruncatedText } from "@carbon6/c6-frontend-design";
import type { RegistryDatasetRef } from "@/api";
import { DatasetPreview } from "@/components/dataset/DatasetPreview";
import { RegistryDatasetRefLink } from "./RegistryDatasetRefLink";
import styles from "./DatasetRefCell.module.css";

// 학습·평가 표의 Dataset 셀. 대표 섬네일과 원본 링크를 표시한다. Dataset 목록 표와 같은
// 표기를 쓴다. 같은 데이터셋을 두 화면에서 다르게 보여 줄 이유가 없다.
//
// 섬네일은 응답의 원본 참조가 포함한 서명 URL 이다. 그래서 셀은 따로 조회하지 않는다. 셀이
// 행마다 조회하면 목록에서 요청 수가 행 수만큼 늘어난다(rules §7 network N+1).
//
// 원본 참조가 없으면 전달받은 이름을 평문으로 표시한다. 말줄임과 추가 건수의 공간은 이 셀이
// 함께 관리한다. 호출부 스타일에 맡기면 어떤 표에서는 이름이 건수 영역까지 넘어온다.

export type DatasetRefCellProps = {
  registryRef?: RegistryDatasetRef | null;
  fallbackLabel?: string;
  // 대표 밖 입력 수. 이름과 따로 표시한다. 한 문자열로 합치면 잘림이 이 수를 먼저 자른다.
  // 그러면 여러 데이터셋을 섞은 행이 하나만 쓴 행과 같아 보인다. 0 이면 자리를 쓰지 않는다.
  extraCount?: number;
  // 주면 추가 건수를 버튼으로 그린다. 누르면 전체 입력을 보는 화면을 연다. 없으면 글자만 둔다.
  onExtraClick?: () => void;
  // 서명 URL 이 만료되어 이미지를 못 읽으면 부모가 목록을 다시 조회한다. 이 콜백이 없으면
  // 이니셜을 표시한다.
  onThumbnailError?: () => void;
};

export function DatasetRefCell({
  registryRef,
  fallbackLabel = "—",
  extraCount = 0,
  onExtraClick,
  onThumbnailError,
}: DatasetRefCellProps): ReactNode {
  const extraTitle = `Dataset 총 ${extraCount + 1}건`;
  return (
    <div className={styles.cell}>
      {registryRef ? (
        <>
          <DatasetPreview
            label={registryRef.display_name}
            imageUrl={registryRef.thumbnail_url ?? null}
            mode="table"
            onImageError={onThumbnailError}
          />
          <RegistryDatasetRefLink registryRef={registryRef} className={styles.name} />
        </>
      ) : (
        <TruncatedText className={styles.name}>{fallbackLabel}</TruncatedText>
      )}
      {extraCount <= 0 ? null : onExtraClick ? (
        // 표의 행 클릭은 중첩 버튼을 건너뛴다(c6 rowActivation). 그래서 전파를 따로 막지 않는다.
        <button
          type="button"
          className={styles.extraButton}
          title={`${extraTitle} 모두 보기`}
          onClick={onExtraClick}
        >
          외 {extraCount}건
        </button>
      ) : (
        <span className={styles.extra} title={extraTitle}>
          외 {extraCount}건
        </span>
      )}
    </div>
  );
}
